import { t } from "@lingui/core/macro"
import {
  queryOptions,
  useSuspenseQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { Outlet, createFileRoute, Navigate, useNavigate } from "@tanstack/react-router"
import { Settings2, Eye } from "lucide-react"
import { useState, useCallback, useContext, useEffect } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { z } from "zod"
import type { ProjectStatus } from "@/features/project-status/core/projectStatus"
import type { StoreConfigurationFormValues } from "@/features/store-configuration/components/StoreDataForm"
import type { StoreMetadata } from "@/features/store-configuration/core/types/store-metadata"
import type { ProjectVersion } from "@/types"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import { projectsTable } from "@/db/schema"
import { ConfigVersionPickerDialog } from "@/features/config-preview/components/ConfigVersionPickerDialog"
import { useTypedFlag } from "@/features/feature-flags/registry"
import { ConfigVersionSelectorDialog } from "@/features/project-editor/components/ConfigVersionSelectorDialog"
import {
  ProjectEditContext,
  type ProjectEditContextValue,
} from "@/features/project-editor/context/ProjectEditContext"
import {
  saveStoreMetadata,
  getStores,
  createStore,
  linkProjectToStore,
  getStoreConfiguration,
  getProjectWithConfigVersion,
} from "@/features/project-editor/server/projectServer"
import { EditModeToggle } from "@/features/project-presence/components/EditModeToggle"
import { LockBanner } from "@/features/project-presence/components/LockBanner"
import { PresenceIndicator } from "@/features/project-presence/components/PresenceIndicator"
import { useProjectPresence } from "@/features/project-presence/hooks/useProjectPresence"
import { StatusDropdown } from "@/features/project-status/components/StatusDropdown"
import { StoreDataSheet } from "@/features/store-configuration/components/StoreDataSheet"
import { StoreInfoButton } from "@/features/store-configuration/components/StoreInfoButton"
import { StoreSelectionDialog } from "@/features/store-configuration/components/StoreSelectionDialog"
import { usePermissions } from "@/hooks/usePermissions"
import { projectQueryKeys } from "@/lib/queryKeys"

// Re-export the hook for backwards compatibility
export { useProjectEditContext } from "@/features/project-editor/context/ProjectEditContext"

const QueryParams = z.object({
  projectId: z.string(),
})

const SearchParams = z.object({
  previewConfigVersion: z.string().optional(),
  step: z.enum(["crop", "detect", "optimize"]).optional(),
  startEditing: z.boolean().optional(),
})

export const projectQuery = (params: z.infer<typeof QueryParams>) =>
  queryOptions({
    queryKey: projectQueryKeys.detail(params.projectId),
    queryFn: () => getProjectWithConfigVersion({ data: params }),
  })

// This file now defines the layout route for the edit steps
// Keep explicit path matching the file path relative to 'routes'
export const Route = createFileRoute("/(authenticated)/projects/edit/$projectId")({
  params: QueryParams,
  validateSearch: SearchParams,
  component: ProjectEditLayout,
  loader: ({ context, params }) => {
    void context.queryClient.ensureQueryData(projectQuery(params))
  },
  // Add error/pending components if desired
})

function ConfigVersionDropdown({
  configVersion,
  configLocale,
  onChangeVersion,
  onPreviewVersion,
}: {
  configVersion?: { id: string; versionLabel: string }
  configLocale?: string | null
  onChangeVersion: () => void
  onPreviewVersion: () => void
}) {
  const { value: configPreview } = useTypedFlag("config-preview")
  const versionLabel = configVersion ? configVersion.versionLabel : t`No config`
  const summary = configLocale ? `${versionLabel} · ${configLocale.toUpperCase()}` : versionLabel
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          aria-label={summary}
          title={summary}
        >
          <Settings2 className="size-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem disabled className="text-xs font-normal text-muted-foreground">
          {summary}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onChangeVersion}>
          <Settings2 className="mr-2 h-4 w-4" />
          {t`Change version`}
        </DropdownMenuItem>
        {configPreview && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onPreviewVersion}>
              <Eye className="mr-2 h-4 w-4" />
              {t`Preview with different version...`}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function ProjectEditLayout() {
  const params = Route.useParams()

  return (
    <ProjectEditLayoutProvider projectId={params.projectId}>
      <ProjectEditLayoutInner />
    </ProjectEditLayoutProvider>
  )
}

function ProjectEditLayoutProvider({
  projectId,
  children,
}: {
  projectId: string
  children: React.ReactNode
}) {
  const { data } = useSuspenseQuery(projectQuery({ projectId }))
  const { project } = data as { project: typeof projectsTable.$inferSelect; configVersion: unknown }
  const queryClient = useQueryClient()
  const params = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate()

  // Feature flags
  const { value: showStatusPresence } = useTypedFlag("project-status-presence")
  const { value: configPreview } = useTypedFlag("config-preview")

  // Presence tracking - only enabled when feature flag is on
  const { viewers, lockHolder, isLocked, isCurrentUserEditing, startEditing, stopEditing } =
    useProjectPresence({
      projectId: params.projectId,
      enabled: showStatusPresence,
    })

  // Auto-start editing when coming from crop (via ?startEditing=true param)
  useEffect(() => {
    if (search.startEditing && showStatusPresence && !isCurrentUserEditing && !isLocked) {
      startEditing()
      // Clear the param to avoid re-triggering on refresh
      void navigate({
        to: ".",
        search: { ...search, startEditing: undefined },
        replace: true,
      })
    }
  }, [
    search.startEditing,
    showStatusPresence,
    isCurrentUserEditing,
    isLocked,
    startEditing,
    navigate,
    search,
  ])

  // Local state for status (used for optimistic updates)
  const [projectStatus, setProjectStatus] = useState<ProjectStatus>(
    (project.status as ProjectStatus) || "draft",
  )

  // Sync local state when project.status changes (e.g., from refetch or another user's update)
  useEffect(() => {
    setProjectStatus((project.status as ProjectStatus) || "draft")
  }, [project.status])

  // Access control: Block store_manager and external_viewer from project edit screens
  const { hasPermission, role, isLoading: permissionsLoading } = usePermissions()
  const canAccessEditScreen =
    hasPermission("project", "update").allowed || role === "store_designer"

  // Determine readOnly mode: user must have permission AND (when presence is enabled) have explicitly started editing
  const canEdit = hasPermission("project", "update").allowed
  const readOnly = !canEdit || (showStatusPresence && !isCurrentUserEditing)

  // Keyboard shortcut: "e" to enter edit mode (but not exit)
  useHotkeys(
    "e",
    () => {
      startEditing()
    },
    {
      enabled: showStatusPresence && canEdit && !isCurrentUserEditing && !isLocked,
    },
    [showStatusPresence, canEdit, isCurrentUserEditing, isLocked, startEditing],
  )

  // State for dialogs
  const [showStoreDialog, setShowStoreDialog] = useState(false)
  const [showStoreDataSheet, setShowStoreDataSheet] = useState(false)
  const [showConfigSelector, setShowConfigSelector] = useState(false)
  const [showConfigPreview, setShowConfigPreview] = useState(false)

  // Handle config preview by navigating to optimize page with preview config version
  const handlePreviewVersion = useCallback(
    (targetVersionId: string) => {
      setShowConfigPreview(false)
      void navigate({
        to: "/projects/edit/$projectId/optimize",
        params: { projectId: params.projectId },
        search: { previewConfigVersion: targetVersionId },
      })
    },
    [navigate, params.projectId],
  )

  // Query for stores list
  const { data: stores = [] } = useQuery({
    queryKey: ["stores"],
    queryFn: () => getStores(),
  })

  // Query for current project's store configuration
  const { data: storeConfig } = useQuery({
    queryKey: ["store-config", params.projectId],
    queryFn: () => getStoreConfiguration({ data: { projectId: params.projectId } }),
  })

  // Mutations
  const createStoreMutation = useMutation({
    mutationFn: createStore,
    onSuccess: (result) => {
      // Link the new store to the project
      void linkProjectMutation.mutate({
        data: { projectId: params.projectId, storeId: result.storeId },
      })
    },
  })

  const linkProjectMutation = useMutation({
    mutationFn: linkProjectToStore,
    onSuccess: () => {
      setShowStoreDialog(false)
      // Refresh store configuration
      void queryClient.invalidateQueries({
        queryKey: ["store-config", params.projectId],
      })
    },
  })

  // Access control early return - MUST be after all hooks to avoid React Rules of Hooks violation
  // Don't redirect while permissions are still loading to avoid flash redirect on page refresh
  if (!permissionsLoading && !canAccessEditScreen) {
    return <Navigate to="/projects" />
  }

  // Extract store metadata from legacy project metadata or new store config
  const currentMetadata = project.metadata as ProjectVersion & {
    storeMetadata?: StoreMetadata
  }
  const storeMetadata = storeConfig?.storeMetadata || currentMetadata?.storeMetadata

  // Handle store metadata saving (legacy support)
  const handleSaveStoreMetadata = async (data: StoreConfigurationFormValues) => {
    if (!storeConfig?.store?.id) {
      throw new Error(
        `Store ID is missing for project ${params.projectId}; link a store before saving configuration`,
      )
    }

    await saveStoreMetadata({
      data: {
        projectId: params.projectId,
        storeMetadata: data,
      },
    })
  }

  // Handle store selection
  const handleSelectStore = (storeId: string) => {
    linkProjectMutation.mutate({
      data: { projectId: params.projectId, storeId },
    })
  }

  // Handle creating new store
  const handleCreateStore = (storeData: {
    name: string
    city: string
    fullAddress: string
    countryCode?: string
  }) => {
    createStoreMutation.mutate({ data: storeData })
  }

  // Build status actions for EditorHeader
  const statusActions = (
    <>
      {/* Edit Mode Toggle - behind feature flag, only for users with update permission */}
      {showStatusPresence && canEdit && (
        <EditModeToggle
          isEditing={isCurrentUserEditing}
          isLocked={isLocked}
          onStartEditing={startEditing}
          onStopEditing={stopEditing}
        />
      )}
      {/* Status Dropdown - behind feature flag */}
      {showStatusPresence && (
        <StatusDropdown
          projectId={params.projectId}
          currentStatus={projectStatus}
          onStatusChange={(newStatus) => {
            setProjectStatus(newStatus)
            void queryClient.invalidateQueries({
              queryKey: projectQueryKeys.detail(params.projectId),
            })
          }}
          disabled={readOnly}
        />
      )}
      {/* Presence Indicator - behind feature flag */}
      {showStatusPresence && viewers.length > 0 && (
        <PresenceIndicator viewers={viewers} lockHolderUserId={lockHolder?.userId} maxVisible={3} />
      )}
      {/* Config Version Dropdown */}
      <ConfigVersionDropdown
        configVersion={
          (
            project as unknown as {
              configurationVersion?: {
                id: string
                versionLabel: string
              }
            }
          ).configurationVersion
        }
        configLocale={project.configLocale}
        onChangeVersion={() => setShowConfigSelector(true)}
        onPreviewVersion={() => setShowConfigPreview(true)}
      />
      {/* Store Info Button */}
      {storeConfig?.store ? (
        <StoreInfoButton
          storeName={storeConfig.store.name}
          currentStoreMetadata={storeMetadata}
          onClick={() => setShowStoreDataSheet(true)}
          compact
        />
      ) : readOnly ? (
        <Badge variant="outline" className="gap-1.5">
          {t`No store linked`}
        </Badge>
      ) : (
        <StoreInfoButton
          currentStoreMetadata={storeMetadata}
          onClick={() => setShowStoreDialog(true)}
          compact
        />
      )}
    </>
  )

  // Context value for child routes
  const lockedBy =
    isLocked && lockHolder ? { userId: lockHolder.userId, userName: lockHolder.userName } : null

  const contextValue: ProjectEditContextValue = {
    projectId: params.projectId,
    projectName: project.name,
    readOnly,
    lockedBy,
    statusActions,
    // Presence
    showStatusPresence,
    canEdit,
    isCurrentUserEditing,
    isLocked,
    startEditing,
    stopEditing,
    viewers,
    lockHolder: lockHolder ? { userId: lockHolder.userId, userName: lockHolder.userName } : null,
    // Project status
    projectStatus,
    onStatusChange: (newStatus) => {
      setProjectStatus(newStatus)
      void queryClient.invalidateQueries({
        queryKey: projectQueryKeys.detail(params.projectId),
      })
    },
    // Config version
    configVersion: (
      project as unknown as {
        configurationVersion?: { id: string; versionLabel: string }
      }
    ).configurationVersion,
    configLocale: project.configLocale,
    onChangeConfigVersion: () => setShowConfigSelector(true),
    onPreviewConfigVersion: () => setShowConfigPreview(true),
    // Store
    storeName: storeConfig?.store?.name ?? null,
    storeBranchNumber: storeConfig?.store?.branchNumber ?? null,
    storeStreetName: storeConfig?.storeMetadata?.streetName ?? null,
    projectAuthor: project.author,
    projectCreationDate: project.date,
    storeMetadata: storeMetadata ?? null,
    storeCountryId: storeConfig?.store?.countryId ?? null,
    storeRegionCode: storeConfig?.store?.regionCode ?? null,
    storeSize: storeConfig?.configuration?.storeSize ?? null,
    storeVersion: storeConfig?.store?.version ?? null,
    hasStore: !!storeConfig?.store,
    onStoreInfoClick: () => {
      if (storeConfig?.store) {
        setShowStoreDataSheet(true)
      } else if (!readOnly) {
        setShowStoreDialog(true)
      }
    },
  }

  return (
    <ProjectEditContext.Provider value={contextValue}>
      {children}
      {/* Store Selection Dialog - only shown for users with update permission */}
      {!readOnly && (
        <StoreSelectionDialog
          open={showStoreDialog}
          onOpenChange={setShowStoreDialog}
          onSelectStore={handleSelectStore}
          onCreateNewStore={handleCreateStore}
          existingStores={stores}
          isLoading={createStoreMutation.isPending || linkProjectMutation.isPending}
        />
      )}
      {/* Store Data Sheet - Controlled version */}
      <StoreDataSheet
        open={showStoreDataSheet}
        onOpenChange={setShowStoreDataSheet}
        projectId={params.projectId}
        projectName={storeConfig?.store?.name || project.name}
        storeId={storeConfig?.store?.id}
        configurationId={storeConfig?.configuration?.id}
        currentStoreMetadata={storeMetadata}
        onSave={handleSaveStoreMetadata}
        readOnly={readOnly}
      />
      {/* Config Version Selector Dialog */}
      <ConfigVersionSelectorDialog
        open={showConfigSelector}
        onOpenChange={setShowConfigSelector}
        projectId={params.projectId}
        currentConfigVersionId={project.configVersionId}
        currentConfigLocale={project.configLocale}
        readOnly={readOnly}
      />
      {/* Config Version Picker Dialog */}
      {configPreview && (
        <ConfigVersionPickerDialog
          open={showConfigPreview}
          onOpenChange={setShowConfigPreview}
          currentConfigVersionId={project.configVersionId}
          onPreview={handlePreviewVersion}
        />
      )}
    </ProjectEditContext.Provider>
  )
}

function ProjectEditLayoutInner() {
  const context = useContext(ProjectEditContext)

  return (
    <div className="flex h-[calc(100vh-theme(space.16))] flex-col">
      {/* Lock banner when another user is editing */}
      {context?.lockedBy && <LockBanner lockedByName={context.lockedBy.userName} />}
      {/* Child routes render their own EditorHeader */}
      <div className="flex-grow overflow-hidden">
        <Outlet />
      </div>
    </div>
  )
}
